import { all, call, put, takeLatest } from 'redux-saga/effects';
import qs from 'qs';
import envConfig from '../../../config';
import {
  fetchEmail,
  fetchNotes,
  fetchActivities,
  fetchPulseLogs,
  fetchFiles,
  fetchLoadMoreFiles,
  clearFiles,
  fetchFilesTimeline,
  updateActivityFiles,
  deleteActivityFiles,
  bookmarkActivityFiles,
  loadMoreActivities,
  loadMoreNotes,
  loadMorePulseLogs,
  fetchSurveyResponsesByEntity,
  bookmarkSurvey,
  // fetchActivityByEmailMessages,
} from '../../routines';
import { request } from '../../../common/util/RestClient';
import {
  getAdditionalFilters,
  mapEmailFiltersToQueryParams,
} from '../../../common/util/activityfeedFilterUtils';
import { convertFilterToVfl } from '../../../common/util/velarisFilterLanguageUtils';
import SUCCESS_MESSAGES from '../../../common/constants/SuccessMessagesConstants';
import { SUCCESS_PLAN_LEVEL } from '../../../common/constants/SuccessPlanConstants';
import {
  fetchSuccessPlanFiles,
  fetchSuccessPlanFilesV2,
} from '../../routines/successPlanRoutines';
import { GLOBAL_DEFAULT_PAGE_SIZE } from '../../../common/constants/CommonConstants';

const getPaginationAndFilters = (
  { page = 1, pageSize = GLOBAL_DEFAULT_PAGE_SIZE, query, filters } = {},
  URL
) => {
  const params = { page, pageSize };

  if (filters && filters.length) {
    params.filters = convertFilterToVfl(filters);
  }

  if (query) {
    params.query = query;
  }

  const queryString = qs.stringify(params);
  const urlWithParams = `${URL}&${queryString}`;
  return request(urlWithParams);
};

export function* fetchEmailSaga({ payload }) {
  const { level, entityId, filters } = payload;

  try {
    const additionalQueryParams = getAdditionalFilters(
      filters,
      mapEmailFiltersToQueryParams
    );

    const URL = `${envConfig.APIG_URL}email-v3/threads?level=${level}&entity=${entityId}${additionalQueryParams}`;
    const { data: emailThreads } = yield call(request, URL);

    const _emailThreads = emailThreads.map((thread) => ({
      type: 'email',
      orderBy: thread.lastUpdatedTime,
      data: thread,
    }));

    yield put(fetchEmail.success({ emailThreads: _emailThreads }));
  } catch (error) {
    yield put(fetchEmail.failure({ error }));
  }
}

export function* fetchNotesSaga({ payload }) {
  try {
    const { level, entityId, paginationParams, filters, query } = payload;
    const params = {
      ...paginationParams,
    };

    if (filters && filters.length > 0) {
      params.filters = convertFilterToVfl(filters);
    }

    if (query && query.length > 0) {
      params.query = query;
    }
    let URL = `${envConfig.APIG_URL}core/notes?level=${level}&parentIdentifier=${entityId}`;
    if (Object.keys(params).length > 0) {
      URL = `${URL}&${qs.stringify(params)}`;
    }
    const response = yield call(request, URL);
    yield put(fetchNotes.success(response));
  } catch (error) {
    yield put(fetchNotes.failure({ error }));
  }
}

export function* loadMoreNotesSaga({ payload = {} }) {
  try {
    const {
      level,
      entityId,
      page = 1,
      pageSize = GLOBAL_DEFAULT_PAGE_SIZE,
      query,
      filters,
    } = payload;
    const URL = `${envConfig.APIG_URL}core/notes?level=${level}&parentIdentifier=${entityId}`;
    const response = yield call(
      getPaginationAndFilters,
      { page, pageSize, query, filters },
      URL
    );
    yield put(loadMoreNotes.success(response));
  } catch (error) {
    yield put(loadMoreNotes.failure({ error }));
  }
}

export function* fetchActivitiesSaga({ payload }) {
  try {
    const { level, entityId, paginationParams, filters, query } = payload;
    const params = {
      ...paginationParams,
    };

    if (filters && filters.length > 0) {
      params.filters = convertFilterToVfl(filters);
    }

    if (query && query.length > 0) {
      params.query = query;
    }
    let URL = `${envConfig.APIG_URL}core/entity-action/activity?level=${level}&parentIdentifier=${entityId}`;
    if (Object.keys(params).length > 0) {
      URL = `${URL}&${qs.stringify(params)}`;
    }
    const response = yield call(request, URL);

    yield put(fetchActivities.success(response));
  } catch (error) {
    yield put(fetchActivities.failure({ error }));
  }
}

export function* loadMoreActivitiesSaga({ payload = {} }) {
  try {
    const {
      level,
      entityId,
      page = 1,
      pageSize = GLOBAL_DEFAULT_PAGE_SIZE,
      query,
      filters,
    } = payload;
    const URL = `${envConfig.APIG_URL}core/entity-action/activity?level=${level}&parentIdentifier=${entityId}`;
    const response = yield call(
      getPaginationAndFilters,
      { page, pageSize, query, filters },
      URL
    );
    yield put(loadMoreActivities.success(response));
  } catch (error) {
    yield put(loadMoreActivities.failure({ error }));
  }
}

export function* fetchFilesSaga({ payload }) {
  try {
    const { level, entityId, paginationParams, filters, query } = payload;
    const params = {
      ...paginationParams,
    };

    if (filters && filters.length > 0) {
      params.filters = convertFilterToVfl(filters);
    }

    if (query && query.length > 0) {
      params.query = query;
    }

    let URL = `${envConfig.APIG_URL}core/files?level=${level}&parentIdentifier=${entityId}`;
    if (Object.keys(params).length > 0) {
      URL = `${URL}&${qs.stringify(params)}`;
    }

    const response = yield call(request, URL);
    yield put(fetchFiles.success(response));
  } catch (error) {
    yield put(fetchFiles.failure({ error }));
  }
}

export function* fetchFilesTimelineSaga({ payload }) {
  try {
    const { level, entityId } = payload;
    const URL = `${envConfig.APIG_URL}core/files/timeline?level=${level}&parentIdentifier=${entityId}`;
    const response = yield call(request, URL);
    yield put(fetchFilesTimeline.success(response));
  } catch (error) {
    yield put(fetchFilesTimeline.failure({ error }));
  }
}

export function* fetchLoadMoreFilesSaga({ payload }) {
  try {
    const { level, entityId, pagination, filters, query } = payload;
    const params = {
      ...pagination,
    };
    if (filters && filters.length > 0) {
      params.filters = convertFilterToVfl(filters);
    }

    if (query && query.length > 0) {
      params.query = query;
    }

    let URL = `${envConfig.APIG_URL}core/files?level=${level}&parentIdentifier=${entityId}`;
    if (Object.keys(params).length > 0) {
      URL = `${URL}&${qs.stringify(params)}`;
    }

    const response = yield call(request, URL);
    yield put(fetchLoadMoreFiles.success(response));
  } catch (error) {
    yield put(fetchLoadMoreFiles.failure({ error }));
  }
}

export function* updateActivityFilesSaga({ payload }) {
  const { comment, fileGroupId, level, entityId, onSuccess } = payload;
  try {
    const body = {
      comment: comment.note,
    };

    const URL = `${envConfig.APIG_URL}core/files/${fileGroupId}`;
    const config = {
      method: 'POST',
      payload: body,
    };
    const response = yield call(request, URL, config);

    yield put(
      updateActivityFiles.success({
        response: response.data,
        successMessage: SUCCESS_MESSAGES.UPDATE_FILE,
        fileGroupId,
      })
    );

    if (level === SUCCESS_PLAN_LEVEL) {
      yield put(
        fetchSuccessPlanFiles.trigger({
          level,
          entityId,
        })
      );
      yield put(
        fetchSuccessPlanFilesV2.trigger({
          level,
          entityId,
        })
      );
    }

    if (onSuccess) onSuccess();
  } catch (error) {
    yield put(updateActivityFiles.failure({ error, fileGroupId }));
  }
}

export function* clearFilesSaga() {
  try {
    yield put(clearFiles.success());
  } catch (error) {
    yield put(clearFiles.failure({ error }));
  }
}

export function* deleteActivityFilesSaga({ payload }) {
  try {
    const { level, entityId, fileGroupId, onSuccess } = payload;
    const URL = `${envConfig.APIG_URL}core/files/${fileGroupId}`;
    const response = yield call(request, URL, {
      method: 'DELETE',
    });
    yield put(
      deleteActivityFiles.success({
        response,
        successMessage: SUCCESS_MESSAGES.DELETE_FILE,
      })
    );
    if (onSuccess) onSuccess();
    if (level === SUCCESS_PLAN_LEVEL) {
      // this should remove once success plan files v2 is fully implemented
      yield put(
        fetchSuccessPlanFiles.trigger({
          level,
          entityId,
        })
      );
      yield put(
        fetchSuccessPlanFilesV2.trigger({
          level,
          entityId,
        })
      );
    }
  } catch (error) {
    yield put(deleteActivityFiles.failure({ error }));
  }
}

export function* bookmarkActivityFilesSaga({ payload }) {
  const { formData, fileGroupId, entityId, level } = payload;
  try {
    const URL = `${envConfig.APIG_URL}core/files/${fileGroupId}/bookmark`;
    const { bookmarked } = formData;
    const body = {
      bookmarkStatus: bookmarked,
    };
    const config = {
      method: 'PATCH',
      payload: body,
    };
    const response = yield call(request, URL, config);

    yield put(
      bookmarkActivityFiles.success({
        response,
        bookmarked,
        successMessage:
          bookmarked === 1
            ? SUCCESS_MESSAGES.BOOKMARK_FILE
            : SUCCESS_MESSAGES.REMOVE_BOOKMARK_FILE,
      })
    );
    if (level === SUCCESS_PLAN_LEVEL) {
      yield put(
        fetchSuccessPlanFiles.trigger({
          level,
          entityId,
        })
      );
    }
  } catch (error) {
    yield put(bookmarkActivityFiles.failure({ error }));
  }
}

export function* fetchPulseLogsSaga({ payload }) {
  try {
    const { level, entityId, paginationParams, filters, query } = payload;
    const params = {
      ...paginationParams,
    };

    if (filters && filters.length > 0) {
      params.filters = convertFilterToVfl(filters);
    }

    if (query && query.length > 0) {
      params.query = query;
    }
    let URL = `${envConfig.APIG_URL}core/entity-action/pulse?level=${level}&parentIdentifier=${entityId}`;
    if (Object.keys(params).length > 0) {
      URL = `${URL}&${qs.stringify(params)}`;
    }
    const response = yield call(request, URL);
    yield put(fetchPulseLogs.success(response));
  } catch (error) {
    yield put(fetchPulseLogs.failure({ error }));
  }
}

export function* fetchLoadMorePulseLogsSaga({ payload }) {
  try {
    const {
      level,
      entityId,
      page = 1,
      pageSize = GLOBAL_DEFAULT_PAGE_SIZE,
      query,
      filters,
    } = payload;
    const URL = `${envConfig.APIG_URL}core/entity-action/pulse?level=${level}&parentIdentifier=${entityId}`;
    const response = yield call(
      getPaginationAndFilters,
      { page, pageSize, query, filters },
      URL
    );
    yield put(loadMorePulseLogs.success(response));
  } catch (error) {
    yield put(loadMorePulseLogs.failure({ error }));
  }
}

// export function* getActivitySagaByEmailMessagesSaga({ payload }) {
//   try {
//     const { formData } = payload;
//     const URL = `${envConfig.APIG_URL}core/entity-action/activity/messages`;
//
//     const config = {
//       method: 'POST',
//       payload: formData,
//     };
//     const response = yield call(request, URL, config);
//
//     yield put(fetchActivityByEmailMessages.success({ response }));
//   } catch (error) {
//     const noAlert = error.response.data.data?.message === 'Not Found';
//
//     yield put(fetchActivityByEmailMessages.failure({ error, noAlert }));
//   }
// }

export function* fetchSurveyResponsesByEntitySaga({ payload }) {
  const SURVEY_MANAGEMENT_BASE_URL = `${envConfig.APIG_URL}survey-management`;
  try {
    const {
      level,
      entityId,
      lastFetchedNPSKey,
      lastFetchedCSATKey,
      filters,
      searchText,
    } = payload;
    let url = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/responses/${level}/entity/${entityId}`;
    const params = {
      lastFetchedCSATKey,
      lastFetchedNPSKey,
      limit: 200,
      searchText,
      ...(filters && filters.length > 0
        ? { filters: convertFilterToVfl(filters) }
        : {}),
    };

    url = `${url}?${qs.stringify(params)}`;

    const response = yield call(request, url);

    yield put(
      fetchSurveyResponsesByEntity.success({
        ...response.data,
        preservePreviousList: payload.preservePreviousList,
      })
    );
  } catch (error) {
    yield put(fetchSurveyResponsesByEntity.failure({ error }));
  } finally {
    yield put(fetchSurveyResponsesByEntity.fulfill());
  }
}
export function* bookmarkSurveySaga({ payload }) {
  const SURVEY_MANAGEMENT_BASE_URL = `${envConfig.APIG_URL}survey-management`;
  try {
    const { level, entityId, id, status, version, type } = payload;
    const URL = `${SURVEY_MANAGEMENT_BASE_URL}/surveys/bookmark/${type}/${id}?status=${status}&version=${version}`;
    const config = {
      method: 'PATCH',
      payload: {},
    };
    const response = yield call(request, URL, config);
    yield put(bookmarkSurvey.success(response));
    yield put(fetchSurveyResponsesByEntity.trigger({ level, entityId }));
  } catch (err) {
    yield put(bookmarkSurvey.failure({ error: err }));
  }
}

export default function* root() {
  yield all([takeLatest(fetchNotes.TRIGGER, fetchNotesSaga)]);
  yield all([takeLatest(fetchActivities.TRIGGER, fetchActivitiesSaga)]);
  yield all([takeLatest(fetchPulseLogs.TRIGGER, fetchPulseLogsSaga)]);
  yield all([takeLatest(fetchEmail.TRIGGER, fetchEmailSaga)]);
  yield all([takeLatest(fetchFiles.TRIGGER, fetchFilesSaga)]);
  yield all([takeLatest(fetchFilesTimeline.TRIGGER, fetchFilesTimelineSaga)]);
  yield all([takeLatest(fetchLoadMoreFiles.TRIGGER, fetchLoadMoreFilesSaga)]);
  yield all([takeLatest(updateActivityFiles.TRIGGER, updateActivityFilesSaga)]);
  yield all([takeLatest(clearFiles.TRIGGER, clearFilesSaga)]);
  yield all([takeLatest(deleteActivityFiles.TRIGGER, deleteActivityFilesSaga)]);
  yield all([
    takeLatest(bookmarkActivityFiles.TRIGGER, bookmarkActivityFilesSaga),
  ]);
  yield all([
    takeLatest(
      fetchSurveyResponsesByEntity.TRIGGER,
      fetchSurveyResponsesByEntitySaga
    ),
  ]);
  yield all([takeLatest(bookmarkSurvey.TRIGGER, bookmarkSurveySaga)]);
  yield all([takeLatest(loadMoreActivities.TRIGGER, loadMoreActivitiesSaga)]);
  yield all([takeLatest(loadMoreNotes.TRIGGER, loadMoreNotesSaga)]);
  yield all([
    takeLatest(loadMorePulseLogs.TRIGGER, fetchLoadMorePulseLogsSaga),
  ]);
  // yield all([
  //   takeLatest(
  //     fetchActivityByEmailMessages.TRIGGER,
  //     getActivitySagaByEmailMessagesSaga
  //   ),
  // ]);
}
